import { sha256 } from '@noble/hashes/sha2.js'
import { transferUpload } from './uploadTransfer'
import type { CaptureConfig } from './types'

type Request = <T>(path: string, init?: RequestInit) => Promise<T>
export type UploadRequest = { purpose: 'source' | 'angle' | 'final'; title?: string; context?: string; project_id?: string; label?: string; clip_id?: string; version_id?: string; delivery_id?: string; return_revision?: number; capture_config?: CaptureConfig }
type Session<T> = { id: string; state: 'uploading' | 'completing' | 'complete' | 'aborted' | 'aborting'; part_size: number; parts: { number: number; size: number; checksum: string }[]; result: T }
const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })

function base64(bytes: Uint8Array) {
  let value = ''
  for (let offset = 0; offset < bytes.length; offset += 16384) {
    value += String.fromCharCode(...bytes.subarray(offset, offset + 16384))
  }
  return btoa(value)
}

// Read bounded chunks even when a very large recording needs multi-GB parts.
export async function checksum(blob: Blob, signal?: AbortSignal) {
  const digest = sha256.create()
  const reader = blob.stream().getReader()
  const abort = () => { void reader.cancel().catch(() => {}) }
  signal?.addEventListener('abort', abort, { once: true })
  try {
    while (true) {
      signal?.throwIfAborted()
      const { done, value } = await reader.read()
      signal?.throwIfAborted()
      if (done) return base64(digest.digest())
      digest.update(value)
    }
  } finally { signal?.removeEventListener('abort', abort); await reader.cancel(); reader.releaseLock(); digest.destroy() }
}

function saved(key: string) {
  try { return localStorage.getItem(key) } catch { return null }
}
function remember(key: string, id: string | null) {
  try { if (id) localStorage.setItem(key, id); else localStorage.removeItem(key) } catch { /* Private browsing can disable persistence. */ }
}

async function retry<T>(run: () => Promise<T>, signal: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    signal.throwIfAborted()
    try { return await run() }
    catch (error) {
      const status = (error as { status?: number }).status
      if (signal.aborted || attempt >= 3 || (status && status < 500 && status !== 408 && status !== 429)) throw error
      await new Promise<void>((resolve, reject) => {
        const abort = () => { clearTimeout(timer); signal.removeEventListener('abort', abort); reject(signal.reason) }
        const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve() }, [500, 1500, 4000][attempt])
        signal.addEventListener('abort', abort, { once: true })
        if (signal.aborted) abort()
      })
    }
  }
}

// Return null only when the workspace explicitly uses local uploads. A failed
// direct transfer must never silently start a second copy through the API.
export async function uploadDirect<T>(request: Request, file: File, body: UploadRequest, onProgress?: (fraction: number) => void, externalSignal?: AbortSignal): Promise<T | null> {
  externalSignal?.throwIfAborted()
  const capability = await request<{ direct: boolean; scope: string; header_preflight?: boolean }>('/api/uploads/capabilities', { signal: externalSignal })
  externalSignal?.throwIfAborted()
  if (capability.header_preflight) {
    const header = new Uint8Array(await file.slice(0, 64 * 1024 + 16).arrayBuffer())
    externalSignal?.throwIfAborted()
    await request('/api/uploads/preflight', { ...post({ header_base64: base64(header), size_bytes: file.size }), signal: externalSignal })
    externalSignal?.throwIfAborted()
  }
  if (!capability.direct) return null
  const controller = new AbortController()
  const { signal } = controller
  const abort = () => controller.abort(externalSignal?.reason)
  externalSignal?.addEventListener('abort', abort, { once: true })
  if (externalSignal?.aborted) abort()
  let key: string | undefined, path: string | undefined
  let completing = false, cancellationPending = false
  try {
    onProgress?.(0)
    const edge = 1024 * 1024
    const fingerprint = await checksum(new Blob([file.slice(0, edge), file.slice(Math.max(edge, file.size - edge))]), signal)
    const identity = JSON.stringify({ scope: capability.scope, ...body, filename: file.name, size_bytes: file.size, last_modified: file.lastModified, fingerprint })
    key = 'motion.upload.' + await checksum(new Blob([identity]), signal)
    const id = saved(key) || crypto.randomUUID()
    remember(key, id)
    path = `/api/uploads/${id}`
    // Await creation even if cancelled: the server may already have created
    // its session. DELETE only after its response prevents orphan sessions.
    const session = await retry(() => request<Session<T>>('/api/uploads', post({ ...body, id, filename: file.name, size_bytes: file.size, last_modified: file.lastModified })), signal)
    path = `/api/uploads/${session.id}`
    if (session.state === 'aborted') {
      remember(key, null)
      throw new Error('This upload was cancelled. Select the recording again to restart it.')
    }
    if (session.state === 'aborting') {
      cancellationPending = true
      throw new DOMException('Upload cancelled.', 'AbortError')
    }
    if (session.state === 'complete') { remember(key, null); onProgress?.(1); return session.result }
    completing = session.state === 'completing'
    if (session.state === 'uploading') {
      signal.throwIfAborted()
      const total = Math.ceil(file.size / session.part_size)
      const existing = new Map(session.parts.map((part) => [part.number, part]))
      const bytes = new Map<number, number>()
      const report = () => onProgress?.(Math.min(1, [...bytes.values()].reduce((sum, value) => sum + value, 0) / file.size))
      let next = 1
      async function transfer() {
        while (next <= total) {
          signal.throwIfAborted()
          const number = next++
          const part = file.slice((number - 1) * session.part_size, Math.min(number * session.part_size, file.size))
          const hash = await checksum(part, signal)
          const remote = existing.get(number)
          if (remote && (remote.size !== part.size || remote.checksum !== hash)) {
            await request(path!, { method: 'DELETE' })
            remember(key!, null)
            throw new Error('This recording has changed since its upload started. Select it again to upload the updated file.')
          }
          if (!remote) await retry(async () => {
            bytes.set(number, 0); report()
            const signed = await request<{ url: string; headers: Record<string, string> }>(`${path}/parts/${number}`, { ...post({ checksum: hash }), signal })
            const response = await transferUpload(signed.url, { method: 'PUT', headers: signed.headers, body: part, credentials: 'omit', signal }, (fraction) => { bytes.set(number, fraction * part.size); report() })
            if (!response.ok) {
              const error = new Error(`The recording upload was interrupted (${response.status}). Select the same file to resume.`)
              throw Object.assign(error, { status: response.status === 403 ? 503 : response.status })
            }
          }, signal)
          bytes.set(number, part.size); report()
        }
      }
      const transfers = await Promise.allSettled([transfer(), transfer()].map((work) => work.catch((error) => { controller.abort(error); throw error })))
      const failure = transfers.find((result) => result.status === 'rejected')
      if (failure?.status === 'rejected') throw failure.reason
      signal.throwIfAborted()
      completing = true
    }
    // Completion is idempotent but cannot be rolled back. Keep its response
    // observable when a cancellation races the final part acknowledgement.
    externalSignal?.removeEventListener('abort', abort)
    onProgress?.(1)
    const result = await retry(() => request<T>(`${path}/complete`, post()), new AbortController().signal)
    remember(key, null)
    return result
  } catch (error) {
    if ((externalSignal?.aborted || cancellationPending) && !completing && path) {
      try {
        await retry(() => request(path!, { method: 'DELETE' }), new AbortController().signal)
        if (key) remember(key, null)
      } catch {
        throw new Error('Transfer stopped, but cancellation could not be confirmed. Select the same file again to finish cleanup.')
      }
      throw new DOMException('Upload cancelled.', 'AbortError')
    }
    throw error
  } finally {
    externalSignal?.removeEventListener('abort', abort)
  }
}
